// src/helpers/generateDoc.js
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import logo from "../assets/images/logo.png";

pdfMake.vfs = window.pdfMake.vfs;

const loadImageBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      const canvas = document.createElement("canvas");
      canvas.width = this.width;
      canvas.height = this.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(this, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });

const BRAND = {
  primary: "#a60805",      // accent
  primarySoft: "#bfe1ff",  // soft background
  ink: "#0F172A",          // text
  subtle: "#64748B",       // secondary text
  line: "#E2E8F0",         // separators
  tableHeader: "#F1F5F9",  // header cells
  zebra: "#ffffff",        // odd rows
};

/** Utilities */
const fmtMoney = (n) => {
  const x = Number(n || 0);
  const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
const fmtQty = (n) => {
  const s = String(Number(n ?? 0));
  const fixed = Number(s).toFixed(2);
  return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};
const toFr = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString("fr-MA");
};
function numberToFrenchWords(n) {
  if (n === 0) return "zéro";
  const U = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"];
  const D = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
  const H = (x) => (x === 0 ? "" : x === 1 ? "cent" : U[x] + " cent" + (x > 1 ? "s" : ""));
  const under100 = (x) => {
    if (x < 17) return U[x];
    const d = Math.floor(x / 10), u = x % 10;
    if (d === 7 || d === 9) return D[d] + (u === 1 ? " et " : "-") + (u ? U[10 + u] : U[10]);
    if (d === 8 && u === 0) return "quatre-vingts";
    if (u === 1 && d >= 1 && d <= 6) return D[d] + " et un";
    return D[d] + (u ? "-" + U[u] : "");
  };
  const under1000 = (x) => {
    const c = Math.floor(x / 100), r = x % 100;
    if (!c) return under100(r);
    return r ? `${H(c)} ${under100(r)}` : H(c);
  };
  let out = "";
  const m = Math.floor(n / 1_000_000);
  const k = Math.floor((n % 1_000_000) / 1000);
  const r = n % 1000;
  if (m) out += (m === 1 ? "un million" : under1000(m) + " millions");
  if (k) out += (out ? " " : "") + (k === 1 ? "mille" : under1000(k) + " mille");
  if (r) out += (out ? " " : "") + under1000(r);
  return out;
}
const amountToWordsMAD = (n) =>
  numberToFrenchWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

/** Map seller (name + subtitle centered header) */
function mapSeller(s) {
  return {
    name: s?.name || "",
    subtitle: s?.subtitle || "",
  };
}

// Build the two footer lines from sellerModel (or seller)
function buildFooterLines(fromSeller) {
  const s = fromSeller || {};
  // line 1: address - phone1[-phone2] - email
  const address = s.address ? String(s.address).replace(/\r?\n/g, ", ").trim() : null;
  const phones = [s.phone1, s.phone2].filter(Boolean).join("-");
  const email = s.email || null;
  const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

  // line 2: RC: x - Patente: y - IF: z - CNSS: w (only show what exists)
  const regs = [
    s.ice ? `ICE: ${s.ice}` : null,
    s.rc ? `RC: ${s.rc}` : null,
    s.patente ? `Patente: ${s.patente}` : null,
    s.identificationFiscale ? `IF: ${s.identificationFiscale}` : null,
    s.cnss ? `CNSS: ${s.cnss}` : null,
  ].filter(Boolean).join(" - ");

  return { line1, regs };
}


/** Map buyer/doc from Matjar payload */
function mapFromMatjar(doc) {

  const items = (doc.lignes || []).map((l) => {
    const unitTTC = Number(l.prixUnitaireTTC ?? 0);
    const unitHT = Number(l.prixUnitaireHT ?? 0);
    const totalHT = Number(l.montantHT);
    const totalTVA = Number(l.montantTVA);
    const totalTTC = Number(l.montantTTC);
    const sub = [l.articleCode, l.unite, l.remise ? `Remise: ${l.remise}` : null]
      .filter(Boolean).join(" • ");
    return {
      label: l.articleNom || "",
      sublabel: sub || undefined,
      qty: Number(l?.quantiteLivree ?? l?.quantite ?? 0),
      unit: l?.unite || "",
      unitHT, unitTTC, totalHT, totalTVA, totalTTC,
    };
  });

  const buyer = {
    name: doc.clientNom || "",
    lines: [
      //doc.clientAdresse,
      doc.clientIce ? `ICE: ${doc.clientIce}` : null,
      doc.clientTelephone1 ? `Tél: ${doc.clientTelephone1}` : null,
      //doc.clientTelephone2 ? `Tél 2: ${doc.clientTelephone2}` : null,
      //doc.clientEmail ? `Email: ${doc.clientEmail}` : null,
    ].filter(Boolean),
  };

  const meta = {
    number: doc.numero || "",
    date: toFr(doc.dateOperation || doc.dateCreation),
    payment: doc.modeReglement || undefined,
    transporteur: doc.transporteurName,
  };

  return { items, buyer, meta };
}

export async function generateDoc(docType, data = {}) {

  const logoBase64 = await loadImageBase64(logo);

  const tMap = {
    devis: "DEVIS",
    bon_commande: "BON DE COMMANDE",
    bon_livraison: "BON DE LIVRAISON",
    facture: "FACTURE",
  };

  const {
    // Either pass matjarDoc + sellerModel …
    matjarDoc,
    sellerModel,
    // …or pass pre-mapped:
    seller,
    buyer,
    meta,
    items,

    // Options
    priceMode,// TTC or HT
    showPrices = docType !== "bon_livraison",
    open = true,
    download = false,
  } = data;

  // Build entities
  const s = mapSeller(sellerModel || seller || {});
  let docBuyer = buyer, docMeta = meta, docItems = items;

  const sellerForFooter = sellerModel || seller || {};
  const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(sellerForFooter);

  if (matjarDoc) {
    const m = mapFromMatjar(matjarDoc);
    docBuyer = m.buyer;
    docMeta = {
      number: m.meta.number,
      date: m.meta.date,
      payment: m.meta.payment,
    };
    docItems = m.items;
  }

  // Items table rows (TTC by default for display)
  const headerRow = showPrices
    ? [
      { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
      { text: priceMode === 'HT' ? "P.U. HT" : "P.U. TTC", alignment: "right", style: "th", width: 80, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: priceMode === 'HT' ? "Total HT" : "Total TTC", alignment: "right", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
    ]
    : [
      { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
      { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
    ];

  const bodyRows = (docItems || []).map((it, i) =>
    showPrices
      ? [
        { text: String(i + 1), alignment: "center" },
        [
          { text: it.label, bold: true, color: BRAND.ink },
          it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
        ],
        { text: fmtQty(it.qty), alignment: "center" },
        { text: it.unit || "", alignment: "center" },
        { text: priceMode === 'HT' ? fmtMoney(it.unitHT) : fmtMoney(it.unitTTC), alignment: "right" },
        { text: priceMode === 'HT' ? fmtMoney(it.totalHT) : fmtMoney(it.totalTTC), alignment: "right" },
      ]
      : [
        { text: String(i + 1), alignment: "center" },
        [
          { text: it.label, bold: true, color: BRAND.ink },
          it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
        ],
        { text: fmtQty(it.qty), alignment: "center" },
        { text: it.unit || "", alignment: "center" },                      // ✅ NOUVEAU
      ]
  );

  const paymentLabel = (mode) => {
    switch (mode) {
      case "CARTE_BANCAIRE":
        return "Carte bancaire";
      case "ESPECES":
        return "Espèces";
      case "CHEQUE":
        return "Chèque";
      case "VIREMENT":
        return "Virement";
      case "TRAITE":
        return "Traite";
      default:
        return mode || "-";
    }
  };

  const widths = showPrices ? [22, "*", 70, 60, 80, 90] : [22, "*", 70, 60];

  // Totals (unchanged logic)
  const sum = (arr, f) => (arr || []).reduce((s, it) => s + (Number(f(it)) || 0), 0);
  const grandHT = showPrices ? matjarDoc.montantTotalHT : 0;
  const grandTVA = showPrices ? matjarDoc.montantTotalTaxe : 0;
  const grandTTC = showPrices ? matjarDoc.montantTotalTTC : 0;
  const amountText = amountToWordsMAD(grandTTC);

  const CARD_W = 250;        // width of each rounded card
  const PADDING_X = 10;      // inner padding left/right
  const PADDING_Y = 10;      // inner padding top/bottom
  const RADIUS = 8;
  const MIN_CARD_H = 100;    // minimum height so cards never look cramped

  // compute a safe shared height (simple & stable)
  const leftRows = 1 + 3; // title + 3 lines (Numéro, Date, Paiement)
  const rightRows = 1 + 1 + (docBuyer?.lines?.length || 0); // title + name + lines
  const LINE_H = 14;        // approx per line
  const CARD_H = Math.max(MIN_CARD_H, 20 + (Math.max(leftRows, rightRows) * LINE_H));

  const stroke = '#000000';
  const fill = '#FFFFFF';
  const dd = {
    pageMargins: [40, 10, 40, 84],
    content: [
      // TOP-CENTER SELLER NAME + SUBTITLE
      {
        columns: [
          // LEFT : LOGO
          {
            width: "*",
            image: logoBase64,
            fit: [120, 90],
            alignment: "left",
          },

          // RIGHT : DOCUMENT META
          {
            width: 170,
            stack: [
              {
                text: tMap[docType] || "DOCUMENT",
                style: "docTitle",
                alignment: "left",
              },
              {
                text: `N° ${docMeta?.number || "-"}`,
                bold: true,
                alignment: "left",
              },
              {
                text: `Date facturation: ${docMeta?.date || "-"}`,
                alignment: "left",
                color: BRAND.subtle,
              },
            ],
          },
        ],
        margin: [0, 0, 0, 12],
      },


      // Separator
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BRAND.line }], margin: [0, 6, 0, 10] },

      // TWO CARDS BELOW: LEFT (Doc data) | RIGHT (Buyer)
      // === TWO CARDS BELOW (equal height, black border, white background, rounded corners, gap) ===
      {
        table: {
          widths: ['*', 12, '*'],
          body: [[
            // LEFT CARD
            {
              stack: [
                // 1) draw rounded rect
                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                // 2) overlay content INTO the rect using negative top margin
                {
                  margin: [PADDING_X, -(CARD_H - PADDING_Y) /* pull up */, PADDING_X, 0],
                  stack: [
                    { text: "Société", bold: true, margin: [0, 0, 0, 6] },
                    { text: sellerForFooter.name || "", bold: true },
                    sellerForFooter.address ? { text: sellerForFooter.address } : null,
                    sellerForFooter.phone1 ? { text: `Tél : ${sellerForFooter.phone1}` } : null,
                    sellerForFooter.email ? { text: `Email : ${sellerForFooter.email}` } : null,

                    // sellerForFooter.ice ? { text: `ICE : ${sellerForFooter.ice}` } : null,
                    // sellerForFooter.rc ? { text: `RC : ${sellerForFooter.rc}` } : null,
                    // sellerForFooter.identificationFiscale
                    //   ? { text: `IF : ${sellerForFooter.identificationFiscale}` }
                    //   : null,
                    // sellerForFooter.cnss ? { text: `CNSS : ${sellerForFooter.cnss}` } : null,
                  ].filter(Boolean)
                },
                // 3) keep cell total height equal to CARD_H
                { text: '', margin: [0, 15, 0, 0] }
              ],
            },

            // GAP
            { text: '', border: [false, false, false, false] },

            // RIGHT CARD
            {
              stack: [
                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                {
                  margin: [PADDING_X, -(CARD_H - PADDING_Y), PADDING_X, 0],
                  stack: [
                    { text: (docType === "bon_livraison" ? "Livrer à" : "Facturer à"), bold: true, margin: [0, 0, 0, 6], color: BRAND.ink },
                    { text: (docBuyer?.name || ""), bold: true, color: BRAND.ink, margin: [0, 0, 0, 2] },
                    ...((docBuyer?.lines || []).map((l) => ({ text: l, color: BRAND.subtle }))),
                  ]
                },
                { text: '', margin: [0, 15, 0, 0] }
              ],
            },
          ]],
        },
        // wrapper table shows no lines; rounded borders are from canvas
        layout: {
          defaultBorder: false,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
        },
        margin: [0, 10, 0, 1],
      },

      // ITEMS TABLE
      {
        table: {
          headerRows: 1,
          widths,
          body: [headerRow, ...bodyRows],
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex === 0 ? BRAND.tableHeader : rowIndex % 2 === 0 ? null : BRAND.zebra),
          hLineWidth: () => 0.6,
          vLineWidth: () => 0,
          hLineColor: () => BRAND.line,
          paddingTop: () => 7,
          paddingBottom: () => 7,
        },
        margin: [3, 40, 0, 0],
      },

      // === REST OF DOCUMENT UNCHANGED ===

      // Totals card (same visual as before)
      (showPrices ? {
        columns: [
          { width: "*", text: "" },
          {
            width: 220,
            table: {
              widths: ["*", 80],
              body:
                docType === 'facture'
                  ? [
                    [
                      { text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                      { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink },
                    ],
                    [
                      { text: "TVA (20%)", alignment: "right", color: BRAND.subtle },
                      { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink },
                    ],
                    [
                      { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                      { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                    ],
                  ]

                  : (docType === 'devis' || docType === 'bon_commande')
                    ? priceMode === 'HT'
                      ? [
                        [
                          { text: "Total HT", bold: true, alignment: "right", color: BRAND.ink },
                          { text: fmtMoney(grandHT), bold: true, alignment: "right", color: BRAND.ink },
                        ],
                      ]
                      : [
                        [
                          { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                          { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                        ],
                      ]

                    : [
                      [
                        { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                        { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                      ],
                    ],
            },
            layout: {
              fillColor: () => BRAND.primarySoft,
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingTop: () => 10,
              paddingBottom: () => 10,
              paddingLeft: () => 10,
              paddingRight: () => 10,
            },
            margin: [0, 14, 0, 8],
          },
        ],
      } : null),

      (matjarDoc?.modeReglement || docMeta?.payment)
        ? {
          text: `Mode de paiement : ${paymentLabel(matjarDoc?.modeReglement || docMeta?.payment)}`,
          margin: [0, 12, 0, 2],
          bold: true,
          color: BRAND.ink,
        }
        : null,

      // Amount in words (kept)
      showPrices ? {
        text: `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
        color: BRAND.subtle,
        fontSize: 9,
        margin: [0, 6, 0, 0],
      } : null,
    ].filter(Boolean),

    // Footer (simple, keep or replace with your previous one)
    footer: function (currentPage, pageCount) {
      return {
        margin: [40, 0, 40, 20],
        stack: [
          // thin separator
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000' }], margin: [0, 4, 0, 6] },

          // centered company info (from sellerModel)
          {
            alignment: 'center',
            stack: [
              { text: footerLine1 || "", fontSize: 8, color: '#333' },
              { text: footerLine2 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
            ],
          },

          // (optional) page number, keep or remove as you wish
          {
            columns: [
              { width: '*', text: '' },
              { width: 'auto', alignment: 'right', text: `Page ${currentPage} / ${pageCount}`, fontSize: 8, color: '#888' },
            ],
            margin: [0, 4, 0, 0],
          },
        ],
      };
    },

    styles: {
      sellerTopName: { fontSize: 24, bold: true, color: "#074e0dff" },
      sellerTopSub: { fontSize: 10, color: "#0b5e12ff" },
      th: { bold: true },
      footerLine: { fontSize: 8, color: "#666" },
      footerPage: { fontSize: 8, color: "#888" },
      docTitle: {
        fontSize: 18,
        bold: true,
        color: BRAND.primary,
      },
    },
    defaultStyle: { fontSize: 10, },
  };

  const fileName = `${(tMap[docType] || "document")}_${docMetaSafe(docMeta).number}.pdf`;
  if (download) return pdfMake.createPdf(dd).download(fileName);
  if (open) return pdfMake.createPdf(dd).open();
  return dd;

  function docMetaSafe(m) {
    return { number: (m?.number || "").replace(/[^\w/-]/g, "") };
  }
}
